/**
 * Chunked media uploader for CMS videos + oversized images.
 *
 * Founder uploads of module videos hit "Network Error" at ~40-50% on
 * files larger than ~50 MB — the culprit was a single multipart POST
 * that pushed the entire buffer through the ingress in one shot; any
 * ingress body cap or mid-body proxy timeout killed the whole upload.
 *
 * This helper wraps three server endpoints:
 *   POST /admin/cms/media/upload/init      → session (returns upload_id, chunk_size)
 *   POST /admin/cms/media/upload/chunk     → append one chunk (repeats)
 *   POST /admin/cms/media/upload/complete  → finalize + return media doc
 *
 * Each chunk is a small independent POST, so a mid-upload failure
 * retries just that chunk instead of restarting from 0%. Callers get a
 * granular progress callback that reports 0..1 across the whole file.
 */
import { api } from "./api";

const DEFAULT_CHUNK_SIZE = 8 * 1024 * 1024;   // 8 MB — matches server default
const CHUNK_RETRIES = 3;

/**
 * Upload a File in chunks. Returns the media doc from the server on
 * success, or throws with the original axios error on any hard failure.
 *
 * @param {File}   file       File from an <input type=file /> change event.
 * @param {"image"|"video"} kind
 * @param {(pct:number)=>void} [onProgress]  Called with 0..1.
 * @param {string} [alt]                     Optional alt text stored on the media doc.
 */
export async function chunkedMediaUpload(file, kind, onProgress, alt = "") {
  // --- 1. INIT --------------------------------------------------------
  const initFd = new FormData();
  initFd.append("kind", kind);
  initFd.append("filename", file.name || "");
  initFd.append("mime", file.type || "");
  initFd.append("size", String(file.size || 0));
  if (alt) initFd.append("alt", alt);
  const { data: init } = await api.post(
    "/admin/cms/media/upload/init", initFd,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  const upload_id = init.upload_id;
  const chunkSize = init.chunk_size || DEFAULT_CHUNK_SIZE;

  // --- 2. CHUNK LOOP --------------------------------------------------
  const total = file.size;
  const totalChunks = Math.max(1, Math.ceil(total / chunkSize));
  let sent = 0;

  for (let i = 0; i < totalChunks; i++) {
    const start = i * chunkSize;
    const end = Math.min(start + chunkSize, total);
    const blob = file.slice(start, end);

    // Per-chunk retry loop — a transient 5xx / dropped connection on
    // one chunk shouldn't kill the whole 756 MB upload. We retry the
    // chunk up to CHUNK_RETRIES times with an exponential backoff
    // before giving up. Ingress body cap / permission errors (4xx)
    // fail fast — no point retrying.
    let attempt = 0;
    while (true) {
      try {
        const fd = new FormData();
        fd.append("upload_id", upload_id);
        fd.append("index", String(i));
        fd.append("chunk", blob, `chunk-${i}`);
        await api.post("/admin/cms/media/upload/chunk", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          // Per-chunk bytes-sent → whole-file progress
          onUploadProgress: (evt) => {
            const chunkLoaded = evt.loaded || 0;
            const pct = Math.min(1, (sent + chunkLoaded) / total);
            onProgress?.(pct);
          },
        });
        break;   // success — advance to next chunk
      } catch (err) {
        const status = err?.response?.status;
        // Any 4xx (except 408 timeout) is a hard client error — do not retry.
        if (status && status >= 400 && status < 500 && status !== 408) throw err;
        attempt += 1;
        if (attempt >= CHUNK_RETRIES) throw err;
        await new Promise((r) => setTimeout(r, 400 * attempt));   // 400ms, 800ms
      }
    }
    sent = end;
    onProgress?.(sent / total);
  }

  // --- 3. COMPLETE ----------------------------------------------------
  const finFd = new FormData();
  finFd.append("upload_id", upload_id);
  if (alt) finFd.append("alt", alt);
  const { data: media } = await api.post(
    "/admin/cms/media/upload/complete", finFd,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  onProgress?.(1);
  return media;
}
